import React, { useMemo, useEffect, useState } from "react";
import { Checkbox, Divider, Typography, Box } from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import { capitalizeAndCleanUnderscore } from "@utils";
import { noOptionList } from "src/components/collections/helpers";
import SearchBar from "@hyro-components/SearchBar";
import MenuItem from "@mui/material/MenuItem";
import styled from "@emotion/styled";
import { shouldShowSearchField } from "@hyro-components/fields/utils";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const indeterminateIcon = <IndeterminateCheckBoxIcon fontSize="small" />;

const MultiSelectContent = ({
  options,
  selectionOptions,
  testId,
  onChange,
  onSearchChange,
  capitalizeOption = true,
  showSelectAll = true
}) => {
  const [searchText, setSearchText] = useState("");
  const [sortedOptions, setSortedOptions] = useState(options);

  const optionsExist = options?.length > 0;
  const isServerSearch = Boolean(onSearchChange);
  const showSearch = isServerSearch || shouldShowSearchField(options);
  const optionsSignature = options.map(option => `${option.key}\u0001${option.display_key}`).join("\u0000");

  useEffect(() => {
    setSortedOptions(options.toSorted((a, b) => selectionOptions.some(val => val.key === b.key) - selectionOptions.some(val => val.key === a.key)));
  }, [optionsSignature]);

  const filteredOptions = useMemo(() =>
    isServerSearch
      ? sortedOptions
      : sortedOptions?.filter(option => option.display_key?.toLowerCase().includes(searchText?.toLowerCase?.() || "")),
  [sortedOptions, searchText, isServerSearch]
  );

  const handleSearchChange = (text) => {
    setSearchText(text);
    onSearchChange?.(text);
  };

  const handleSelectAll = () => {
    if (selectionOptions.length === filteredOptions.length) {
      onChange([]);
    } else {
      onChange(filteredOptions);
    }
  };

  const handleOptionSelect = (option) => {
    const isSelected = selectionOptions.some(val => val.key === option.key);
    if (isSelected) {
      onChange(selectionOptions.filter(val => val.key !== option.key));
    } else {
      onChange([...selectionOptions, option]);
    }
  };

  return (
    <Box sx={{
      maxHeight: "315px", overflow: "auto", padding: 1.5
    }}>
      {showSearch && <StyledSearchBar>
        <SearchBar stayOpen onChange={handleSearchChange} customWidth="100%" />
      </StyledSearchBar>
      }

      {showSelectAll && optionsExist && filteredOptions.length !== 0 && <StyledMenuItem
        value={"select-all"}
        data-testid={`${testId}-option-select-all`}
        onClick={handleSelectAll}>
        <Checkbox
          icon={icon}
          disableRipple
          checkedIcon={checkedIcon}
          indeterminateIcon={indeterminateIcon}
          indeterminate={filteredOptions.length > selectionOptions.length && selectionOptions.length > 0}
          style={{ marginRight: 4 }}
          checked={selectionOptions.length === filteredOptions.length}
        />
        <Typography variant="body2">
          Select All ({filteredOptions.length})
        </Typography>
      </StyledMenuItem>}

      {showSelectAll && optionsExist && filteredOptions.length !== 0 && <Divider sx={{ borderColor: "rgba(45, 44, 68, 0.12)", marginBlock: "4px !important" }} />}

      {filteredOptions?.map((option, index) => (
        <StyledMenuItem
          key={option.key}
          data-testid={`${testId}-option-${index}`}
          value={option}
          onClick={() => handleOptionSelect(option)}>
          <Checkbox
            icon={icon}
            disableRipple
            checkedIcon={checkedIcon}
            style={{ marginRight: 4 }}
            checked={selectionOptions.find(val => val.key === option.key) !== undefined}
          />
          <Typography variant="body2">
            {capitalizeOption ? capitalizeAndCleanUnderscore(option.display_key) : option.display_key}
          </Typography>
        </StyledMenuItem>
      ))}

      {(filteredOptions.length === 0 || !optionsExist) && noOptionList().map((option) => (
        <StyledMenuItem
          key={option.key}
          data-testid={"no-options"}
          disabled={option.disabled}
          error={option.error}>
          <Typography variant="body2">
            {option.display_key}
          </Typography>
        </StyledMenuItem>
      ))}
    </Box >
  );
};

const StyledMenuItem = styled(MenuItem)(({ theme, error }) => ({
  color: error && theme.palette.error[500],
  padding: "3px 4px",
  borderRadius: "8px"
}));

const StyledSearchBar = styled("div")({
  paddingBottom: "4px",
  width: "100%"
});

export default MultiSelectContent;
